/**
 * Unpaid Time (TAS-586) — the small, pure rules the admin pages share.
 *
 * Unpaid Time is a time-off category with no balance: full 8-hour days only, never paid or
 * exported. It may require the employee's paid time to be used up first; when it isn't, the
 * API refuses with `PAID_NOT_EXHAUSTED` and a `blocking` list, and an admin may override.
 *
 * Every flag is optional on the wire (an older API omits it) and a missing flag must read as
 * today's behaviour: paid, requestable, counts for holiday eligibility.
 *
 * No API/axios imports here, so the rules stay unit-testable under jest.
 */
import { TimeOffPaidGateBlocking } from "../interfaces/TimeOff";

/** Unpaid Time is requested in whole days only, and a day is always this long. */
export const UNPAID_DAY_HOURS = 8;

/** True only when the API says so — a missing flag is a paid category. */
export function isUnpaid(row: { isUnpaid?: boolean } | null | undefined): boolean {
  return row?.isUnpaid === true;
}

/** Holiday eligibility honours the category flag; a missing flag counts, as it always did. */
export function countsForHolidayEligibility(
  row: { countsForHolidayEligibility?: boolean } | null | undefined
): boolean {
  return row?.countsForHolidayEligibility !== false;
}

// ─── Timesheet review: a day's time-off entries ─────────────────────────────

/** The fields of a review day's time-off entry these rules read. */
export interface TimeOffDayLike {
  hours: number;
  status: string;
  isUnpaid?: boolean;
  countsForHolidayEligibility?: boolean;
}

/**
 * Approved PAID time-off hours on a day — what feeds the console's Hol / PTO cell, its week
 * total and KPI. Unpaid Time is never paid, so it never counts here.
 */
export function paidApprovedTimeOffHours(entries: TimeOffDayLike[]): number {
  return entries
    .filter((e) => e.status === "APPROVED" && !isUnpaid(e))
    .reduce((s, e) => s + e.hours, 0);
}

/**
 * Does a day's time off count as "worked" for the holiday adjacent-day rule? APPROVED or
 * PENDING, as before — but only categories that count for holiday eligibility (missing = yes).
 * Mirrors the server's hasApprovedOrPendingOnDate.
 */
export function timeOffCountsTowardHoliday(entries: TimeOffDayLike[]): boolean {
  return entries.some(
    (e) => (e.status === "APPROVED" || e.status === "PENDING") && countsForHolidayEligibility(e)
  );
}

/** Split a day's visible entries into paid and unpaid, so each gets its own chip. */
export function splitUnpaid<T extends { isUnpaid?: boolean }>(entries: T[]): { paid: T[]; unpaid: T[] } {
  const paid: T[] = [];
  const unpaid: T[] = [];
  entries.forEach((e) => (isUnpaid(e) ? unpaid : paid).push(e));
  return { paid, unpaid };
}

/** Hours with the trailing zeros trimmed: 8 → "8", 7.5 → "7.5", 3.25 → "3.25". */
function fmtGateHours(h: number): string {
  if (!Number.isFinite(h)) return "0";
  return String(Math.round(h * 100) / 100);
}

/** "Vacation: 24 h left (counts as used up below 8 h)". */
export function describeBlocking(b: TimeOffPaidGateBlocking): string {
  return `${b.categoryName}: ${fmtGateHours(b.availableHours)} h left (counts as used up below ${fmtGateHours(b.thresholdHours)} h)`;
}

/** Duck-typed so it works on the API's TimeOffActionError without importing the API module. */
export function isPaidNotExhaustedError(err: unknown): err is { code: "PAID_NOT_EXHAUSTED"; message: string; blocking?: TimeOffPaidGateBlocking[] } {
  return !!err && typeof err === "object" && (err as { code?: unknown }).code === "PAID_NOT_EXHAUSTED";
}

/** The blocking list off a PAID_NOT_EXHAUSTED error, never undefined. */
export function blockingOf(err: unknown): TimeOffPaidGateBlocking[] {
  const b = (err as { blocking?: unknown } | null)?.blocking;
  return Array.isArray(b) ? (b as TimeOffPaidGateBlocking[]) : [];
}

/**
 * The server's own message when it sent one (a TimeOffActionError or any Error), else the
 * fallback. The API's messages name the day/category and are what tells the admin what to fix.
 */
export function errorMessageOf(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * The API's explanation for a refused action, when it is one — a 4xx (not pending any more, the
 * timesheet is locked, paid time not used up…). A 5xx or a network failure gets the fallback,
 * so "Internal Server Error" never reaches the admin.
 */
export function refusalMessageOf(err: unknown, fallback: string): string {
  const status = (err as { status?: unknown } | null)?.status;
  if (typeof status === "number" && status >= 400 && status < 500 && err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}

/**
 * Plain-text body for an "Approve anyway?" prompt (window.confirm or a modal detail):
 * the server message, then one line per blocking category.
 */
export function paidOverridePromptText(err: unknown, action: "approve" | "submit"): string {
  const lines = blockingOf(err).map((b) => `• ${describeBlocking(b)}`);
  const head = errorMessageOf(err, "This employee still has paid time off available.");
  const tail = action === "approve"
    ? "Approve the unpaid time anyway? Your name is recorded as overriding the rule."
    : "Submit the unpaid time anyway? Your name is recorded as overriding the rule.";
  return [head, ...lines, "", tail].join("\n");
}

/** Why a balance row can't be requested right now, or null when it can (or the API didn't say). */
export function notRequestableText(row: {
  isRequestable?: boolean;
  notRequestableReason?: string | null;
}): string | null {
  if (row.isRequestable !== false) return null;
  if (row.notRequestableReason === "PAID_NOT_EXHAUSTED") return "Available once paid time off is used up";
  return "Not available to request right now";
}

/** One failed approval in a bulk run. */
export interface BulkApproveFailure {
  employeeName: string;
  message: string;
  /** The API's structured code, when it sent one. */
  code?: string;
}

/**
 * The popup for a bulk approve that had failures — one line per request with the server's
 * reason, instead of a bare count. Bulk never overrides the paid-time rule; that is done one
 * request at a time from the request itself, which the last line says.
 */
export function summarizeBulkApproveFailures(
  total: number,
  failures: BulkApproveFailure[]
): { title: string; message: string } | null {
  if (failures.length === 0) return null;
  const lines = failures.map((f) => `• ${f.employeeName}: ${f.message}`);
  const anyGate = failures.some((f) => f.code === "PAID_NOT_EXHAUSTED");
  // Same title and first line the page always showed; the per-request reasons are new.
  const head = `${failures.length} of ${total} approvals failed.`;
  const hint = anyGate
    ? "To approve unpaid time anyway, approve that request on its own."
    : "";
  return {
    title: "Partial Failure",
    message: [head, ...lines, ...(hint ? ["", hint] : [])].join("\n"),
  };
}
